import { useState, useRef, useEffect } from 'react';

import { motion } from 'framer-motion';

import { REFERENCE_COLOURS } from './reference';
import { ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { ChunkDocumentDrawerContent } from '@/new/components/reference-content/chunk-document-drawer-content';
import { DocumentDrawerContent } from '@/new/components/reference-content/document-drawer-content';
import { FactDocumentDrawerContent } from '@/new/components/reference-content/fact-document-drawer-content';
import { Button } from '@/new/components/shadcn/button';
import { cn } from '@/new/lib/utils';

interface ReferenceSidePanelProps {
  onClose: () => void;
  references: { id: string; index: number; referenceType: ReferenceType }[];
  activeReferenceId: string;
  onSelectReference: (id: string, referenceType: ReferenceType) => void;
}

const MIN_WIDTH_PCT = 0.2;
const MAX_WIDTH_PCT = 0.6;
const DEFAULT_WIDTH_PCT = 0.35;

const getWidthFromPct = (pct: number) => Math.round(window.innerWidth * pct);

// Store width outside component to persist across open/close cycles
let lastPanelWidthPx = getWidthFromPct(DEFAULT_WIDTH_PCT);

export function ReferenceSidePanel({
  onClose,
  references,
  activeReferenceId,
  onSelectReference,
}: ReferenceSidePanelProps) {
  const [widthPx, setWidthPx] = useState(lastPanelWidthPx);
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const panelRight = panelRef.current?.getBoundingClientRect().right ?? window.innerWidth;
      const newPx = panelRight - e.clientX;
      const constrainedPx = Math.min(Math.max(newPx, getWidthFromPct(MIN_WIDTH_PCT)), getWidthFromPct(MAX_WIDTH_PCT));
      lastPanelWidthPx = constrainedPx;
      setWidthPx(constrainedPx);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const activeReference = references.find((ref) => ref.id === activeReferenceId);

  return (
    <motion.div
      ref={panelRef}
      initial={{ width: 0 }}
      animate={{ width: widthPx }}
      exit={{ width: 0 }}
      transition={{ type: 'tween', duration: isResizing ? 0 : 0.2 }}
      className="relative h-full shrink-0"
    >
      {/* Resize handle */}
      <div onMouseDown={handleMouseDown} className="absolute top-0 -left-2.5 z-40 h-full w-5 cursor-ew-resize">
        <div
          className={cn(
            'absolute top-1/2 left-1/2 z-20 flex h-7 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-0.75 rounded-md border border-gray-300 duration-150 hover:bg-gray-100',
            isResizing ? 'bg-gray-100' : 'bg-white',
          )}
        >
          <Icon className="lucide--chevrons-left-right text-xs text-gray-500" />
        </div>
      </div>

      <div
        className={cn(
          'flex h-full flex-col overflow-hidden border-l bg-white',
          isResizing ? 'border-teal-400' : 'border-gray-200',
          'transition-colors duration-300',
        )}
      >
        {/* Reference list bar */}
        <div className="flex items-center justify-between gap-2 border-b">
          {references.length > 0 && (
            <div className="flex w-full gap-1 overflow-x-auto mask-r-from-95% p-2.5 py-2.25 pr-5 [scrollbar-width:none]">
              {references.map((ref) => (
                <button
                  key={ref.id}
                  onClick={() => onSelectReference(ref.id, ref.referenceType)}
                  className={cn(
                    'font-inter shrink-0 rounded-md border px-2 py-0.5 text-xs ring-offset-1 transition-colors duration-150 focus-visible:ring-1 focus-visible:outline-none',
                    REFERENCE_COLOURS[ref.referenceType],
                    ref.id === activeReferenceId ? '' : 'opacity-50 hover:opacity-80',
                  )}
                >
                  {ref.index}
                </button>
              ))}
            </div>
          )}

          <Button onClick={onClose} variant={'ghost'} size={'iconSm'} className="mr-2">
            <Icon className="lucide--panel-right-close" />
          </Button>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-hidden">
          {activeReference?.referenceType === ReferenceType.Chunk && (
            <ChunkDocumentDrawerContent chunkId={activeReferenceId} />
          )}
          {activeReference?.referenceType === ReferenceType.Document && (
            <DocumentDrawerContent docId={activeReferenceId} />
          )}
          {activeReference?.referenceType === ReferenceType.Fact && (
            <FactDocumentDrawerContent factId={activeReferenceId} />
          )}
        </div>
      </div>
    </motion.div>
  );
}
